import React from 'react';
import sensibilisationImg from '../assets/images/Parlons-santé-mentale-grande-cause-nationale.jpg.webp';
import { useStrapi } from '../services/useStrapi';
import { getModuleSensibilisation } from '../services/api';

const FALLBACK = {
  titre: 'SENSIBILISATION À LA SANTÉ MENTALE',
  description: `Destinée à tous les salariés, équipes terrain, agents, techniciens et personnels administratifs,
cette formation permet de mieux comprendre la santé mentale, de repérer les situations de
vulnérabilité et d'adopter les premiers réflexes de soutien. Aucun prérequis\u00a0nécessaire.`,
  objectifs: [
    { texte: 'Comprendre ce qu\'est la santé mentale et ses enjeux en milieu professionnel.' },
    { texte: 'Déconstruire les idées reçues et réduire la stigmatisation.' },
    { texte: 'Repérer les signes de mal-être ou de souffrance psychique.' },
    { texte: 'Adopter une posture d\'écoute et de communication bienveillante.' },
    { texte: 'Identifier les relais et ressources internes ou externes.' },
  ],
  sections_programme: [
    { titre: '1. Santé mentale : de quoi parle-t-on ?', description: 'Définition de la santé mentale et des troubles psychiques ; état des lieux ; facteurs de risque et impacts ; les interventions et l\'accompagnement ; les compétences psycho-sociales.' },
    { titre: '2. Troubles psychiques : mieux comprendre pour mieux agir', description: 'Présentation des troubles les plus fréquents (dépression, troubles anxieux, troubles psychotiques, addictions) ; repérer les signes et symptômes ; les situations de crise ; l\'importance des interventions précoces.' },
    { titre: '3. Situations de stress et d\'épuisement professionnel', description: 'Stress, burn-out, bore-out, brown-out ; repérer les signes et symptômes ; quand s\'inquiéter et agir ; les prises en charge adaptées.' },
    { titre: '4. Adopter la bonne posture face à une personne en difficulté', description: 'Écoute active et communication bienveillante ; posture de soutien et limites.' },
    { titre: '5. Ressources et relais', description: 'Rôle de l\'entreprise ; dispositifs internes et ressources externes.' },
  ],
  url_pdf: '/catalogue_helpmind.pdf',
  texte_organisation: 'Durée : 3 à 4 heures (format modulable selon vos besoins) — Présentiel — 6 participants minimum / 15 participants maximum.',
  managers_description: 'Ce module complémentaire est spécialement conçu pour les managers de proximité, encadrants, responsables d\'équipe, chefs de service et RH/référents QVCT. Il approfondit les enjeux spécifiques au rôle managérial face aux situations de mal-être.',
  managers_objectifs: [
    { texte: 'Comprendre le rôle et les limites du manager face aux situations de mal-être.' },
    { texte: 'Repérer les signaux d\'alerte au sein de leurs équipes.' },
    { texte: 'Adopter une posture managériale sécurisante et responsable.' },
    { texte: 'Savoir engager une discussion délicate avec un collaborateur.' },
    { texte: 'Orienter efficacement vers les dispositifs adaptés.' },
  ],
  managers_texte_organisation: 'Durée : 3 heures — 4 participants minimum / 12 participants maximum',
};

const SensibilisationModule = () => {
  const { data, loading } = useStrapi(getModuleSensibilisation);
  const d = data || FALLBACK;

  return (
    <div className="container">
      <section className="section text-center">
        <img src={sensibilisationImg} alt="Sensibilisation à la santé mentale" className="module-icon" />
        <h1>{d.titre}</h1>
        <p className="max-w-800">{d.description}</p>
      </section>

      <section className="section text-center">
        <h2>OBJECTIFS</h2>
        <div className="objectives-box">
          {loading ? (
            <p>Chargement…</p>
          ) : (
            <ul className="objectives-list">
              {d.objectifs.map((o, i) => (
                <li key={i}>{o.texte}</li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="section text-center">
        <h2>PROGRAMME</h2>
        <p>Programme en {d.sections_programme.length} modules</p>
        <div className="program-box">
          {loading ? (
            <p>Chargement…</p>
          ) : (
            <ul className="program-list">
              {d.sections_programme.map((s, i) => (
                <li key={i}>
                  <strong>{s.titre}</strong>
                  <br />
                  {s.description}
                </li>
              ))}
            </ul>
          )}
        </div>
        {d.url_pdf && (
          <div style={{ marginTop: '30px' }}>
            <a href={d.url_pdf} download="Catalogue_HelpMind_Formation.pdf" target="_blank" rel="noopener noreferrer" className="btn">
              📄 TÉLÉCHARGER LE CATALOGUE COMPLET (PDF)
            </a>
          </div>
        )}
      </section>

      <section className="section text-center">
        <h2>ORGANISATION</h2>
        <div className="organisation-box">
          <svg className="org-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p>{d.texte_organisation}</p>
        </div>
      </section>

      <section className="section text-center">
        <div className="objectives-box" style={{ borderColor: '#e07b39', background: 'linear-gradient(135deg, #fff8f4 0%, #fff 100%)' }}>
          <h2 style={{ color: '#e07b39', marginTop: 0 }}>+ MODULE MANAGERS &amp; ENCADRANTS</h2>
          <p style={{ color: '#555', marginBottom: '16px' }}>{d.managers_description}</p>
          {loading ? (
            <p>Chargement…</p>
          ) : (
            <ul className="objectives-list" style={{ textAlign: 'left', display: 'inline-block' }}>
              {d.managers_objectifs.map((o, i) => (
                <li key={i}>{o.texte}</li>
              ))}
            </ul>
          )}
          <p style={{ marginTop: '16px', color: '#777', fontSize: '0.95em' }}>
            {d.managers_texte_organisation}
            <br />
            <em>Ce module peut être déployé seul si les participants ont déjà bénéficié d'une formation sur la santé mentale.</em>
          </p>
        </div>
      </section>
    </div>
  );
};

export default SensibilisationModule;
